import { IClosingActivityEntry } from '../services/closing-activity.service';
import { DatedEntry } from './activity-merge';
import { formatTrailTimeLabel, rawDetailOf, TrailTranslate } from './trail.presenter';

/**
 * The HUMAN half of the activity log → the pure view's shape — REV-21378.
 *
 * The sibling of `mapTrailEntryToActivity` (REV-21869), and deliberately built the same way, because
 * the two halves land in ONE list: if they formatted times differently or resolved keys differently,
 * the seam between them would be visible to the user as an inconsistency rather than to us as a bug.
 * `formatTrailTimeLabel` is therefore REUSED rather than re-implemented.
 *
 * ⚠️ **Returns {@link DatedEntry}, not `ActivityEntry`.** The merge orders by the raw ISO instant;
 * `timeLabel` is a display string ("vor 2 Stunden") and does not sort. See `activity-merge.ts`.
 */
export function mapActivityEntry(entry: IClosingActivityEntry, translate: TrailTranslate): DatedEntry {
    const description =
        entry.detailKey !== null && entry.detailKey.length > 0
            ? translate(entry.detailKey, entry.params ?? {})
            : undefined;

    return {
        at: entry.at,
        entry: {
            // Prefixed, because the merge's tiebreak needs ids that are unique ACROSS both halves —
            // the agent half yields `trail-<sequence>`, which would otherwise be free to collide.
            id: `act-${entry.id}`,
            actor: {
                type: entry.actorType,
                // `actorName` is genuinely absent for machine-caused rows written on this side (an ERP
                // sync). Leaving it undefined lets the pure view fall back to
                // `abcp_activity_actor_unknown` — which is the RIGHT word here, unlike on the trail
                // side where the run is always the author (see `mapTrailEntryToActivity`'s Runde 7).
                ...(entry.actorName !== null && entry.actorName.length > 0 ? { name: entry.actorName } : {}),
            },
            title: translate(entry.titleKey, entry.params ?? {}),
            ...(description !== undefined ? { description } : {}),
            timeLabel: formatTrailTimeLabel(entry.at),
            ...(entry.actorType === 'agent'
                ? {
                      rawDetail: rawDetailOf(entry.titleKey, entry.detailKey, {
                          category: entry.category,
                          ...entry.params,
                      }),
                  }
                : {}),
        },
    };
}

/** One page of the human half, ready for {@link mergeActivitySlices}. */
export function mapActivityEntries(entries: readonly IClosingActivityEntry[], translate: TrailTranslate): DatedEntry[] {
    return entries.map((entry) => mapActivityEntry(entry, translate));
}
